/**
 * AdminDataCleanup — super-admin only "delete test data" tool.
 *
 * Pick a tenant + entity, optional search filter, list rows, multi-select,
 * delete with cascade preview & confirm. Audit-logged on the backend.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Trash2, Search, ShieldAlert, RefreshCw, Loader2, AlertTriangle } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed (${res.status})`);
  }
  return res.json();
};

export default function AdminDataCleanup() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tenantId, setTenantId] = useState('');
  const [entity, setEntity] = useState('students');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState({}); // id → true
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dryRun, setDryRun] = useState(null);

  const { data: tenants = [] } = useQuery({
    queryKey: ['cleanup-tenants'],
    queryFn: () => api('/api/superadmin/tenants?limit=200').then((d) => Array.isArray(d) ? d : (d.tenants || [])),
    staleTime: 60_000,
  });

  const { data: entities = [] } = useQuery({
    queryKey: ['cleanup-entities'],
    queryFn: () => api('/api/admin/data-cleanup/entities'),
    staleTime: 5 * 60_000,
  });

  const { data: list, isFetching, refetch } = useQuery({
    queryKey: ['cleanup-list', entity, tenantId, search],
    queryFn: () => api(`/api/admin/data-cleanup/${entity}/list?tenant_id=${tenantId}&search=${encodeURIComponent(search)}&limit=200`),
    enabled: !!entity && !!tenantId,
  });

  const previewMutation = useMutation({
    mutationFn: () => api(`/api/admin/data-cleanup/${entity}/delete`, {
      method: 'POST',
      body: JSON.stringify({ tenant_id: tenantId, ids: Object.keys(selected).filter((k) => selected[k]), dry_run: true }),
    }),
    onSuccess: (r) => { setDryRun(r); setConfirmOpen(true); },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api(`/api/admin/data-cleanup/${entity}/delete`, {
      method: 'POST',
      body: JSON.stringify({ tenant_id: tenantId, ids: Object.keys(selected).filter((k) => selected[k]) }),
    }),
    onSuccess: (r) => {
      toast.success(`Deleted ${r.deleted}. Cascade: ${Object.values(r.cascade || {}).reduce((a, b) => a + b, 0)}.`);
      setSelected({});
      setConfirmOpen(false);
      setDryRun(null);
      qc.invalidateQueries({ queryKey: ['cleanup-list'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const items = list?.items || [];
  const selCount = Object.values(selected).filter(Boolean).length;
  const allChecked = items.length > 0 && items.every((i) => selected[i.id]);
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else {
      const m = {};
      items.forEach((i) => { m[i.id] = true; });
      setSelected(m);
    }
  };

  const entityCfg = entities.find((e) => e.id === entity);

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6" data-testid="admin-data-cleanup">
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="cleanup-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-rose-600" /> Data Cleanup
            </h1>
            <p className="text-xs text-slate-500">Delete test or stale data per tenant. Audit-logged. Cascades dependent rows.</p>
          </div>
        </div>

        <Card>
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Tenant</Label>
              <Select value={tenantId} onValueChange={setTenantId}>
                <SelectTrigger data-testid="cleanup-tenant-select"><SelectValue placeholder="Select a tenant" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {(tenants || []).map((t) => (
                    <SelectItem key={t.id || t._id} value={String(t.id || t._id)}>{t.name || t.slug || t.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Entity</Label>
              <Select value={entity} onValueChange={(v) => { setEntity(v); setSelected({}); }}>
                <SelectTrigger data-testid="cleanup-entity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {entities.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  className="pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={entityCfg ? `Search ${entityCfg.label.toLowerCase()}…` : 'Search…'}
                  data-testid="cleanup-search-input"
                />
              </div>
            </div>
            {entityCfg?.cascade?.length > 0 && (
              <div className="md:col-span-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                Deleting will also remove dependent rows in: <code className="font-mono">{entityCfg.cascade.join(', ')}</code>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              {entityCfg?.label || entity} {tenantId && `· ${list?.count ?? 0} found`}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => refetch()} data-testid="cleanup-refresh-btn">
                <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={selCount === 0 || previewMutation.isPending}
                onClick={() => previewMutation.mutate()}
                data-testid="cleanup-delete-btn"
              >
                {previewMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                Delete {selCount} selected
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {!tenantId ? (
              <div className="text-sm text-slate-500 p-6 text-center">Pick a tenant to begin.</div>
            ) : isFetching ? (
              <div className="text-sm text-slate-500 p-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
            ) : items.length === 0 ? (
              <div className="text-sm text-slate-500 p-6 text-center">No items match.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="p-2 text-left w-8">
                        <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="cleanup-select-all" />
                      </th>
                      <th className="p-2 text-left">Summary</th>
                      <th className="p-2 text-left">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} className={`border-t hover:bg-slate-50 ${selected[it.id] ? 'bg-rose-50' : ''}`} data-testid={`cleanup-row-${it.id}`}>
                        <td className="p-2">
                          <Checkbox
                            checked={!!selected[it.id]}
                            onCheckedChange={(c) => setSelected({ ...selected, [it.id]: !!c })}
                            data-testid={`cleanup-select-${it.id}`}
                          />
                        </td>
                        <td className="p-2 truncate max-w-md">{it._summary || it.id}</td>
                        <td className="p-2 text-slate-500 text-xs">{(it.created_at || '').slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Confirm dialog with cascade preview */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="cleanup-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-rose-600" /> Confirm permanent delete</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p>You are about to permanently delete <Badge variant="destructive">{dryRun?.would_delete ?? selCount}</Badge> {entityCfg?.label.toLowerCase()}.</p>
            {dryRun?.cascade_preview && Object.keys(dryRun.cascade_preview).length > 0 && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
                <p className="font-semibold mb-1">Cascade preview:</p>
                <ul className="space-y-0.5">
                  {Object.entries(dryRun.cascade_preview).map(([c, n]) => (
                    <li key={c}><code className="font-mono">{c}</code> · {n} row{n === 1 ? '' : 's'}</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-rose-600 font-medium">This cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate()}
              data-testid="cleanup-confirm-btn"
            >
              {deleteMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
